import { AfterContentInit, Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';
import { UntypedFormControl, NgControl } from '@angular/forms';
import { PhoneNumberMaskValidatorService } from '@app/shared/services/phone-number-mask-validator.service';
import { getNextInputValue } from '@app/shared/utilities/input.utility';
import { Subscription } from 'rxjs';

const generalPhoneFieldKeys = [
  'Backspace', // BACKSPACE
  'Tab', // TAB
  'Enter', // ENTER
  'Escape', // ESCAPE
  'Delete', // DELETE'
  'ArrowDown',
  'End',
  'Home',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp'
];

@Directive({
  selector: '[acsPhoneNumberMask]',
  standalone: false
})
export class PhoneNumberMaskDirective implements AfterContentInit, OnDestroy {
  private el: HTMLInputElement;
  private formControl: UntypedFormControl;
  private formChangeSub = new Subscription();
  private prevLength = 0;

  constructor(
    private baseControl: NgControl,
    private elementRef: ElementRef,
    private phoneNumberMaskValidator: PhoneNumberMaskValidatorService
  ) {
    this.el = this.elementRef.nativeElement;
  }

  @HostListener('keydown', ['$event'])
  onKeyUp(event: KeyboardEvent): void {
    this.formControl?.markAsDirty();

    if (this.isGeneralKey(event) || this.isShortcut(event)) {
      return;
    }

    // if the press isn't a single numeric digit
    // block incase isMaskAvailable is false and we don't call stripMasks
    if (!/^\d$/.test(event.key)) {
      event.preventDefault();
      return;
    }

    if (
      this.phoneNumberMaskValidator.isMaskAvailable &&
      this.phoneNumberMaskValidator.stripMask(getNextInputValue(this.el, event.key)).length >
        this.phoneNumberMaskValidator.rawMaskLength
    ) {
      event.preventDefault();
    }
  }

  @HostListener('paste', ['$event'])
  onPaste(event: ClipboardEvent): void {
    const pasteData = event.clipboardData?.getData('text') ?? '';

    if (!pasteData.length) return;

    const nextValue = getNextInputValue(this.el, pasteData);

    if (
      !this.isPhoneMaskCompatible(nextValue) ||
      (this.phoneNumberMaskValidator.isMaskAvailable &&
        this.phoneNumberMaskValidator.stripMask(nextValue).length >
          this.phoneNumberMaskValidator.rawMaskLength)
    ) {
      event.preventDefault();
    }
  }

  ngAfterContentInit(): void {
    this.formControl = this.baseControl.control as UntypedFormControl;

    if (this.phoneNumberMaskValidator.isMaskAvailable) {
      this.formChangeSub.add(
        this.formControl.valueChanges.subscribe((val) => {
          this.maskPhoneValueAndSetToObjects(val);
        })
      );

      if (this.el.value) {
        this.maskPhoneValueAndSetToObjects(this.el.value);
      }
    }
  }

  ngOnDestroy(): void {
    this.formChangeSub.unsubscribe();
  }

  private maskPhoneValueAndSetToObjects(value: string): void {
    const strippedValue = this.phoneNumberMaskValidator.stripMask(value);
    const isDelete = this.prevLength >= strippedValue.length;
    const maskedValue = this.phoneNumberMaskValidator.addMask(value, isDelete);
    // handles edge case where user changes phone mask
    const newStrippedVal = this.phoneNumberMaskValidator.stripMask(maskedValue);
    this.prevLength = strippedValue.length;
    this.el.value = maskedValue;

    this.formControl.patchValue(newStrippedVal, { emitModelToViewChange: false, emitEvent: false });
    this.checkIfFormFieldValid(maskedValue);
  }

  private isGeneralKey(event: KeyboardEvent): boolean {
    return generalPhoneFieldKeys.indexOf(event.key) !== -1;
  }

  private isShortcut(event: KeyboardEvent): boolean {
    const key = event.key.toLowerCase();

    return (
      (event.ctrlKey || event.metaKey) &&
      (key === 'a' || key === 'c' || key === 'v' || key === 'x' || key === 'z')
    );
  }

  private isPhoneMaskCompatible(value: string): boolean {
    // get just the mask characters for the phone mask
    const maskCharacters = (this.phoneNumberMaskValidator.phoneNumberMask ?? '').replace(/#/g, '');

    // Regex ensures every character is either a digit or one of the mask characters
    return value.split('').every((char) => /\d/.test(char) || maskCharacters.includes(char));
  }

  private checkIfFormFieldValid(maskedValue: string): void {
    if (!this.phoneNumberMaskValidator.isPhoneNumberValid(maskedValue, true)) {
      this.formControl.setErrors({ invalid: true });

      // will apply invalid style to the input on page load
      if (this.el !== document.activeElement) {
        this.formControl.markAsTouched();
      }
    } else {
      this.formControl.setErrors(null);
    }
  }
}
